/**
 * Demo session state and the fake token.
 *
 * Nothing here is a credential. The token carries the literal string "demo"
 * where a signature belongs, so `jwt.verify` can never accept it, and no demo
 * account exists in the database. Tampering with this module grants nothing.
 */

import { USER_IDS } from "./ids";

export type DemoRole = "admin" | "employee" | "business" | "client";

const DEMO_ROLE_KEY = "demoRole";

/** Deliberately public: they unlock nothing real. */
export const DEMO_EMAIL = "demo@alnarpsfarm.example";
export const DEMO_PASSWORD = "demo";

/** Demo role -> backend `role` string and fixture user id. */
const ROLE_MAP: Record<DemoRole, { role: string; userId: string }> = {
  admin: { role: "admin", userId: USER_IDS.admin },
  employee: { role: "employee", userId: USER_IDS.employee },
  business: { role: "business", userId: USER_IDS.business },
  client: { role: "social", userId: USER_IDS.client },
};

export function isDemoRole(value: string | null): value is DemoRole {
  return value === "admin" || value === "employee" || value === "business" || value === "client";
}

export function getDemoRole(): DemoRole | null {
  try {
    const stored = sessionStorage.getItem(DEMO_ROLE_KEY);
    return isDemoRole(stored) ? stored : null;
  } catch {
    return null; // sessionStorage can throw in private mode.
  }
}

export function isDemoMode(): boolean {
  return getDemoRole() !== null;
}

/**
 * sessionStorage is not observable by React. Components subscribe here so the
 * banner appears the moment demo mode starts — entering via /demo is a
 * client-side navigation, so nothing remounts on its own.
 */
const listeners = new Set<() => void>();

export function subscribeToDemoRole(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function notifyDemoRoleChanged(): void {
  listeners.forEach((listener) => listener());
}

export function setDemoRole(role: DemoRole): void {
  sessionStorage.setItem(DEMO_ROLE_KEY, role);
  notifyDemoRoleChanged();
}

export function exitDemoMode(): void {
  sessionStorage.removeItem(DEMO_ROLE_KEY);
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  notifyDemoRoleChanged();
}

export function getDemoIdentity(role: DemoRole): { role: string; userId: string } {
  return ROLE_MAP[role];
}

function base64Url(value: string): string {
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Readable by `decodeTokenPayload`, unacceptable to `jwt.verify`. */
export function mintDemoToken(role: DemoRole): string {
  const identity = getDemoIdentity(role);
  const header = base64Url(JSON.stringify({ alg: "none", typ: "JWT" }));
  const payload = base64Url(
    JSON.stringify({ userId: identity.userId, role: identity.role }),
  );
  return `${header}.${payload}.demo`;
}

/** True when the submitted login is the demo login. */
export function isDemoCredentials(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const { email, password } = body as { email?: unknown; password?: unknown };
  return (
    typeof email === "string" &&
    typeof password === "string" &&
    email.trim().toLowerCase() === DEMO_EMAIL &&
    password === DEMO_PASSWORD
  );
}
